import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { authAPI } from '../../api/api';
import { Modal } from '../Common';

const ProfileSettingsModal = ({ isOpen, onClose }) => {
  const { user, updateUser } = useAuth();
  const { t } = useLanguage();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [loginName, setLoginName] = useState(user?.loginName || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const resetPasswordFields = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    resetPasswordFields();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword && newPassword !== confirmPassword) {
      setError(t('profileSettingsModal.passwordMismatch'));
      return;
    }
    if (newPassword && !currentPassword) {
      setError(t('profileSettingsModal.currentPasswordRequired'));
      return;
    }

    setSaving(true);
    try {
      const { data } = await authAPI.updateProfile({ full_name: fullName, loginName });
      updateUser(data.data);

      if (newPassword) {
        await authAPI.changePassword({ currentPassword, newPassword });
      }

      setSuccess(t('profileSettingsModal.updateSuccess'));
      resetPasswordFields();
    } catch (err) {
      setError(err.response?.data?.error || t('profileSettingsModal.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('nav.profileSettings')} size='sm'>
      <form onSubmit={handleSubmit}>
        <div className='space-y-4'>
          {error && (
            <div className='bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-3 py-2'>
              {error}
            </div>
          )}
          {success && (
            <div className='bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-3 py-2'>
              {success}
            </div>
          )}

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('profileSettingsModal.fullName')}</label>
            <input
              type='text'
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className='input-field'
              required
            />
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('users.loginName')}</label>
            <input
              type='text'
              value={loginName}
              onChange={(e) => setLoginName(e.target.value)}
              className='input-field'
              required
            />
          </div>

          <div className='border-t border-gray-100 pt-4'>
            <p className='text-sm font-medium text-gray-700 mb-2'>{t('profileSettingsModal.changePasswordOptional')}</p>
            <div className='space-y-3'>
              <input
                type='password'
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className='input-field'
                placeholder={t('profileSettingsModal.currentPasswordPlaceholder')}
                autoComplete='current-password'
              />
              <input
                type='password'
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className='input-field'
                placeholder={t('profileSettingsModal.newPasswordPlaceholder')}
                autoComplete='new-password'
              />
              <input
                type='password'
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className='input-field'
                placeholder={t('profileSettingsModal.confirmNewPasswordPlaceholder')}
                autoComplete='new-password'
              />
            </div>
          </div>

          <div className='flex justify-end space-x-3 pt-2'>
            <button type='button' onClick={handleClose} className='btn-secondary'>
              {t('common.close')}
            </button>
            <button type='submit' disabled={saving} className='btn-primary disabled:opacity-50'>
              {saving ? t('common.saving') : t('profileSettingsModal.saveChanges')}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ProfileSettingsModal;
